/*
 * Copyright 2024 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import {
  RangeCalendar as AriaRangeCalendar,
  RangeCalendarProps as AriaRangeCalendarProps,
  DateValue
} from 'react-aria-components/RangeCalendar';
import {CalendarGrid, CalendarHeader} from './Calendar';
import {ContextValue, Provider} from 'react-aria-components/slots';
import {createContext, CSSProperties, ForwardedRef, forwardRef, ReactNode} from 'react';
import {forwardRefType, GlobalDOMAttributes} from '@react-types/shared';
import {getAllowedOverrides, StyleProps} from './style-utils' with {type: 'macro'};
import {HeaderContext, HeadingContext} from './Content';
import {helpTextStyles} from './Field';
// @ts-ignore
import intlMessages from '../intl/*.json';
import {style} from '../style' with {type: 'macro'};
import {Text} from 'react-aria-components/Text';
import {useLocalizedStringFormatter} from 'react-aria/useLocalizedStringFormatter';
import {useSpectrumContextProps} from './useSpectrumContextProps';

export interface RangeCalendarProps<T extends DateValue>
  extends
    Omit<
      AriaRangeCalendarProps<T>,
      | 'visibleDuration'
      | 'weeksInMonth'
      | 'style'
      | 'className'
      | 'render'
      | 'children'
      | 'styles'
      | keyof GlobalDOMAttributes
    >,
    StyleProps {
  /**
   * The error message to display when the calendar is invalid.
   */
  errorMessage?: ReactNode;
  /**
   * The number of months to display at once.
   *
   * @default 1
   */
  visibleMonths?: number;
}

export const RangeCalendarContext =
  createContext<ContextValue<Partial<RangeCalendarProps<any>>, HTMLDivElement>>(null);

const calendarStyles = style<{isMultiMonth?: boolean}>(
  {
    display: 'flex',
    containerType: {
      default: 'inline-size',
      isMultiMonth: 'unset'
    },
    flexDirection: 'column',
    gap: 24,
    disableTapHighlight: true,
    '--cell-gap': {
      type: 'paddingStart',
      value: 4
    },
    '--cell-max-width': {
      type: 'width',
      value: 32
    },
    '--cell-responsive-size': {
      type: 'width',
      value: {
        default: '[min(var(--cell-max-width), (100cqw - (var(--cell-gap) * 12)) / 7)]',
        isMultiMonth: '--cell-max-width'
      }
    },
    width: {
      default: 'calc(7 * var(--cell-max-width) + var(--cell-gap) * 12)',
      isMultiMonth: 'fit'
    },
    maxWidth: {
      default: 'full',
      isMultiMonth: 'unset'
    }
  },
  getAllowedOverrides()
);

/**
 * RangeCalendars display a grid of days in one or more months and allow users to select a
 * contiguous range of dates.
 */
export const RangeCalendar = /*#__PURE__*/ (forwardRef as forwardRefType)(function RangeCalendar<
  T extends DateValue
>(props: RangeCalendarProps<T>, ref: ForwardedRef<HTMLDivElement>) {
  [props, ref] = useSpectrumContextProps(props, ref, RangeCalendarContext);
  let {
    visibleMonths = 1,
    errorMessage,
    UNSAFE_style,
    UNSAFE_className,
    styles,
    ...otherProps
  } = props;
  let stringFormatter = useLocalizedStringFormatter(intlMessages, '@react-spectrum/s2');

  let isMultiMonth = visibleMonths > 1;
  return (
    <AriaRangeCalendar
      {...otherProps}
      ref={ref}
      visibleDuration={{months: visibleMonths}}
      style={{...UNSAFE_style, '--num-calendars': visibleMonths} as CSSProperties}
      className={(UNSAFE_className || '') + calendarStyles({isMultiMonth}, styles)}>
      {({isInvalid, isDisabled}) => {
        return (
          <>
            <Provider
              values={[
                [HeaderContext, null],
                [HeadingContext, null]
              ]}>
              <CalendarHeader visibleMonths={visibleMonths} />
            </Provider>
            <div
              className={style({
                display: 'flex',
                flexDirection: 'row',
                gap: 24,
                alignItems: 'start'
              })}>
              {Array.from({length: visibleMonths}).map((_, i) => (
                <CalendarGrid key={i} months={i} />
              ))}
            </div>
            {isInvalid && (
              <Text
                slot="errorMessage"
                className={helpTextStyles({isInvalid, isDisabled, size: 'M'})}>
                {errorMessage ||
                  stringFormatter.format('calendar.invalidSelection', {selectedCount: 2})}
              </Text>
            )}
          </>
        );
      }}
    </AriaRangeCalendar>
  );
});
